/**
 * «A che punto sono i moduli» — la casella che spiega il KPI in alto.
 *
 * Sostituisce «Aree di lavoro», che era una lista piatta dei sette moduli col
 * solo pallino di stato: ripeteva la voce Moduli in barra senza aggiungere
 * niente.
 *
 * ## Perché serve anche col Gantt sopra
 *
 * Il Gantt dice **quando**, non **quanto**. Una barra che finisce il 12 giugno
 * non dice se quel modulo è finito — e infatti la Panoramica di maggio era
 * ingannevole proprio per questo: tutte le barre erano nel passato, quindi la
 * geometria da sola urlava «è tutto fatto» mentre M5 non era mai partito. Il
 * Gantt è il piano, e il piano mente. La percentuale è lo stato.
 *
 * ## Parola + barra, non il numero nudo
 *
 * La barra la si legge in un secondo, la parola («in uso», «anteprima») ci
 * tiene onesti: senza, un «66%» sarebbe la parola *beta* travestita da misura.
 *
 * Solo i **moduli funzionali** (M1-M5): INFRA e LIAISON sono roba nostra e al
 * cliente non vengono mostrati nemmeno in `/moduli`. Il filtro sta nel servizio
 * `avanzamento`, non qui, così il totale in alto conta esattamente queste righe.
 */

import { Link } from "react-router-dom";

import { Skeleton } from "@/components/ui/skeleton";
import { useAvanzamento } from "@/hooks/useAvanzamento";
import { cn } from "@/lib/utils";
import type { ModuloStato } from "@/types/api";

const STATO_LABEL: Record<ModuloStato, string> = {
  roadmap: "in roadmap",
  anteprima: "anteprima",
  beta: "beta",
  live: "in uso",
  dismesso: "dismesso",
};

const STATO_BARRA: Record<ModuloStato, string> = {
  roadmap: "bg-muted-foreground/30",
  anteprima: "bg-warning",
  beta: "bg-primary",
  live: "bg-success",
  dismesso: "bg-muted-foreground/20",
};

export function PuntoModuli() {
  const { data, isLoading } = useAvanzamento();
  const moduli = data?.moduli ?? [];

  return (
    <div className="rounded-lg border bg-card h-full p-5 space-y-4">
      <span className="text-sm font-medium uppercase tracking-wider text-muted-foreground">
        A che punto sono i moduli
      </span>

      {isLoading ? (
        <div className="space-y-4">
          <Skeleton className="h-8 w-full" />
          <Skeleton className="h-8 w-full" />
          <Skeleton className="h-8 w-full" />
        </div>
      ) : moduli.length === 0 ? (
        <p className="text-sm text-muted-foreground">Nessun modulo da mostrare.</p>
      ) : (
        <ul className="space-y-3.5">
          {moduli.map((m) => (
            <li key={m.codice} className="space-y-1.5">
              <div className="flex items-baseline gap-2 text-sm">
                <span className="font-mono text-[11px] text-muted-foreground w-6 shrink-0">
                  {m.codice}
                </span>
                <Link
                  to={`/moduli/${m.slug}`}
                  className="font-medium flex-1 min-w-0 truncate hover:text-brand-700 transition-colors"
                >
                  {m.nome}
                </Link>
                <span className="text-xs text-muted-foreground shrink-0">
                  {STATO_LABEL[m.stato]}
                </span>
                <span className="text-xs font-medium tabular-nums w-9 text-right shrink-0">
                  {m.percentuale}%
                </span>
              </div>
              <div className="h-1.5 w-full rounded-full bg-muted overflow-hidden">
                <div
                  className={cn(
                    "h-full rounded-full transition-[width] duration-500",
                    STATO_BARRA[m.stato]
                  )}
                  style={{ width: `${m.percentuale}%` }}
                  role="progressbar"
                  aria-label={`${m.nome}: ${m.percentuale}%`}
                  aria-valuenow={m.percentuale}
                  aria-valuemin={0}
                  aria-valuemax={100}
                />
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
